import React, { useState } from 'react';
import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { getClienteImportColumns, importClienti } from '../services/api';
import { useMediaQuery } from 'react-responsive';

const ClientiImport = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'danger'
    const [csvColumns, setCsvColumns] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    
    const isMobile = useMediaQuery({ maxWidth: 991 });

    const clienteSchemaFields = [
        { key: 'nome', label: 'Nome', required: true },
        { key: 'cognome', label: 'Cognome', required: true },
        { key: 'denominazione_sociale', label: 'Denominazione Sociale' },
        { key: 'presso', label: 'Presso' },
        { key: 'codice_fiscale', label: 'Codice Fiscale' },
        { key: 'partita_iva', label: 'Partita IVA' },
        { key: 'indirizzo', label: 'Indirizzo' },
        { key: 'cap', label: 'CAP' },
        { key: 'citta', label: 'Città' },
        { key: 'provincia', label: 'Provincia' },
        { key: 'nazione', label: 'Nazione' },
        { key: 'codice_destinatario', label: 'Codice Destinatario' },
        { key: 'telefono', label: 'Telefono' },
        { key: 'cellulare', label: 'Cellulare' },
        { key: 'email', label: 'Email' },
        { key: 'pec', label: 'PEC' },
        { key: 'luogo_nascita', label: 'Luogo di Nascita' },
        { key: 'data_nascita', label: 'Data di Nascita' },
        { key: 'punti_fedelta', label: 'Punti Fedeltà' },
        { key: 'origine', label: 'Origine' }, // Added new field
    ];

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        setMessage('');
        setCsvColumns([]);
        setColumnMapping({});

        if (file) {
            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await getClienteImportColumns(file);
                const columns = response.data;
                setCsvColumns(columns);

                // Attempt to pre-fill mapping based on exact matches
                const initialMapping = {};
                clienteSchemaFields.forEach(field => {
                    const matchedColumn = columns.find(col => col.toLowerCase() === field.label.toLowerCase() || col.toLowerCase() === field.key.toLowerCase());
                    if (matchedColumn) {
                        initialMapping[field.key] = matchedColumn;
                    }
                });
                setColumnMapping(initialMapping);

            } catch (error) {
                console.error('Errore durante il recupero delle colonne:', error);
                setMessage(error.response?.data?.detail || 'Errore durante il recupero delle colonne del file.');
                setMessageType('danger');
            }
        }
    };

    const handleMappingChange = (schemaField, csvColumn) => {
        setColumnMapping(prev => ({
            ...prev,
            [schemaField]: csvColumn === '' ? null : csvColumn,
        }));
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage('Seleziona un file CSV/Excel da importare.');
            setMessageType('danger');
            return;
        }

        // Validate required fields are mapped
        const requiredFieldsMapped = clienteSchemaFields.filter(field => field.required).every(field => columnMapping[field.key]);
        if (!requiredFieldsMapped) {
            setMessage('Assicurati di mappare tutti i campi obbligatori (es. Nome, Cognome).');
            setMessageType('danger');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('mapping', JSON.stringify(columnMapping));

        try {
            const response = await importClienti(selectedFile, columnMapping);
            setMessage(response.data.message);
            setMessageType('success');
            setSelectedFile(null); // Clear selected file after successful upload
            setCsvColumns([]);
            setColumnMapping({});
        } catch (error) {
            console.error('Errore durante l\'importazione del file:', error);
            setMessage(error.response?.data?.detail || 'Errore durante l\'importazione del file.');
            setMessageType('danger');
        }
    };

    return (
        <Container>
            <h2>Importa Clienti da CSV/Excel</h2>
            
            {isMobile && (
                <Alert variant="info" className="mb-3">
                    <div className="small">
                        <strong>💡 Consiglio:</strong> Per operazioni di import complesse e mappatura colonne, si consiglia di utilizzare un computer desktop per una migliore esperienza.
                    </div>
                </Alert>
            )}
            
            {message && <Alert variant={messageType}>{message}</Alert>}
            <Form>
                <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label>Seleziona un file CSV o Excel</Form.Label>
                    <Form.Control 
                        type="file" 
                        accept=".csv, .xls, .xlsx" 
                        onChange={handleFileChange}
                        style={{
                            minHeight: isMobile ? '44px' : 'auto',
                            fontSize: isMobile ? '16px' : 'auto'
                        }}
                    />
                </Form.Group>

                {csvColumns.length > 0 && (
                    <div className="mb-3">
                        <h3 className={isMobile ? 'h5' : 'h3'}>Mappa Colonne</h3>
                        <p className={isMobile ? 'small' : ''}>Associa le colonne del tuo file ai campi del database.</p>
                        {clienteSchemaFields.map(field => {
                            const selectedCsvColumn = columnMapping[field.key];
                            const mappedColumns = Object.values(columnMapping).filter(val => val !== null);
                            const availableCsvColumns = csvColumns.filter(col => 
                                !mappedColumns.includes(col) || col === selectedCsvColumn
                            );

                            return (
                                <Row key={field.key} className="mb-2 align-items-center">
                                    <Col xs={12} md={4} className={`${isMobile ? 'mb-1' : 'text-md-end'}`}>
                                        <Form.Label className={`mb-0 ${isMobile ? 'small fw-bold' : ''}`}>
                                            {field.label} {field.required && <span className="text-danger">*</span>}
                                        </Form.Label>
                                    </Col>
                                    <Col xs={12} md={8}>
                                        <Form.Select
                                            value={selectedCsvColumn || ''}
                                            onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                            style={{
                                                minHeight: isMobile ? '44px' : 'auto',
                                                fontSize: isMobile ? '16px' : 'auto'
                                            }}
                                        >
                                            <option value="">Ignora</option>
                                            {availableCsvColumns.map(col => (
                                                <option key={col} value={col}>{col}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                            );
                        })}
                    </div>
                )}

                <div className={isMobile ? 'd-grid' : ''}>
                    <Button 
                        variant="primary" 
                        onClick={handleUpload} 
                        disabled={!selectedFile || csvColumns.length === 0}
                        className={isMobile ? '' : 'me-2'}
                        style={{
                            minHeight: isMobile ? '44px' : 'auto',
                            fontSize: isMobile ? '16px' : 'auto'
                        }}
                    >
                        Importa
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default ClientiImport;
